import { baseApi } from "@/config/http-common";
import { baseApiLogs } from "@/config/http-logs";
import { baseHelpdeskApi } from "@/config/http-helpdesk";
import { LoaderActions, LoaderGetters } from "@/store/modules/loader";
import {
  AuthActions,
  AuthGetters,
  getAccountSessionToken,
} from "@/store/modules/auth";
import { store } from "@/store";
import router from "@/router";
import { getLocale } from "@/i18n";
import { notifyAssistantHttpError } from "@/services/assistant/AssistantErrorEventBus";

const TIMEOUT = 1000000;
let isRedirectingToLogin = false;

const isSilentRequest = (config: any) => Boolean(config?.silent);

export const onRequestSuccess = async (config: any) => {
  if (!isSilentRequest(config) && !store.getters[LoaderGetters.GET_LOADER]) {
    await store.dispatch(LoaderActions.SET_LOADER, true);
  }

  const token = getAccountSessionToken(
    store.getters[AuthGetters.GET_USER_IDENTITY]
  );
  const locale = getLocale();
  if (token) {
    if (!config.headers) {
      config.headers = {};
    }
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (!config.headers) {
    config.headers = {};
  }
  config.headers["Accept-Language"] = locale;
  config.timeout = TIMEOUT;
  return config;
};

const redirectToLogin = async (onUnauthenticated: () => void) => {
  onUnauthenticated();
  await store.dispatch(AuthActions.LOGOUT);

  if (isRedirectingToLogin) {
    return;
  }

  isRedirectingToLogin = true;

  try {
    if (router.currentRoute.value.name !== "Auth") {
      window.location.replace("/login");
    }
  } finally {
    isRedirectingToLogin = false;
  }
};

export const setupAxiosInterceptors = (onUnauthenticated: () => void) => {
  const onResponseError = async (
    err: {
      status: any;
      config?: any;
      response?: { status?: any; data?: any };
    },
    source: "BASE_API" | "LOGS_API" | "HELPDESK_API"
  ) => {
    notifyAssistantHttpError(err, source);

    const status = err?.status || err?.response?.status;
    const authenticationFailed = status === 401;
    if (!isSilentRequest(err?.config)) {
      await store.dispatch(LoaderActions.SET_LOADER, false);
    }

    if (authenticationFailed) {
      await redirectToLogin(onUnauthenticated);
      return Promise.reject(err);
    }

    return Promise.reject(err);
  };

  const onResponseSuccess = async (response: any) => {
    if (!isSilentRequest(response?.config)) {
      await store.dispatch(LoaderActions.SET_LOADER, false);
    }
    return response;
  };

  if (baseApi.interceptors) {
    baseApi.interceptors.request.use(onRequestSuccess);
    baseApi.interceptors.response.use(
      (response) => onResponseSuccess(response),
      (error) => onResponseError(error, "BASE_API")
    );
  }

  if (baseApiLogs.interceptors) {
    baseApiLogs.interceptors.request.use(onRequestSuccess);
    baseApiLogs.interceptors.response.use(
      (response) => onResponseSuccess(response),
      (error) => onResponseError(error, "LOGS_API")
    );
  }

  if (baseHelpdeskApi.interceptors) {
    baseHelpdeskApi.interceptors.request.use(onRequestSuccess);
    baseHelpdeskApi.interceptors.response.use(
      (response) => onResponseSuccess(response),
      (error) => onResponseError(error, "HELPDESK_API")
    );
  }
};
